import { DefaultPalette, Icon, IconButton, IContextualMenuProps, IIconProps, PrimaryButton, Text } from '@fluentui/react';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { routes } from '../../../../common/constants';
import MessageCourier from '../../../../lib/MessageCourier';
import { defaultCommandBarButtonStyles, titleCommandBarButtonStyles } from '../../../../styles/button-styles';
import TimeTaxSupportSystemClient from '../../../../system';
import ViewWrapperWithCommandBar from '../../../Layout/ViewWrapperWithCommandBar';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { LiquidacionEstado } from '../../Direct/aje/Wizard/LiquidacionEstado';
import { SettlementTablePreliquidadoReportHours } from '../components/SettlementTablePreliquidadoReportHours';
import './detalle-liquidacion-report-hours.css';
import { ReportHoursSettlementEstado, SolicitudExportacionReporteAsincronaList } from '../../../../system/report_hours_settlement/ReportHoursSettlementSystem';

interface IReportHoursSettlementDetailProps {
  system: TimeTaxSupportSystemClient;
  courier: MessageCourier;
  changeBlockUI: (v: boolean) => void;
}

export interface ISettlementItemsGrouped {
  ldi_sector: string;
  ldi_periodo: string;
  promedio_costo_hora: string;
  ldi_horas_confirmadas: string;
  ldi_importe_costo_neto: string;
  ldi_importe_costo_total: string;
  concept: string;
}

const ReportHoursSettlementDetail = (props: IReportHoursSettlementDetailProps) => {
  const { t } = useTranslation();
  const [settlement, setSettlement] = useState<SolicitudExportacionReporteAsincronaList | null>(null);

  let { id } = useParams();
  const navigate = useNavigate();

  const GlobalNavButtonIcon: IIconProps = { iconName: 'GlobalNavButton' };

  const agrupadoresCorporacionLiquidadora = [
    {
      agrcorliq_id: 1,
      agrcorliq_nombre: 'AJEFILMS SERVICIOS',
      agrcorliq_tipo: 'AJE_FILMS_SERVICES',
    },
    {
      agrcorliq_id: 2,
      agrcorliq_nombre: 'AJEFILMS PROYECTOS',
      agrcorliq_tipo: 'AJE_FILMS_PROJECTS',
    },
    {
      agrcorliq_id: 3,
      agrcorliq_nombre: 'ASESORIAS PERU',
      agrcorliq_tipo: 'ASESORIAS_PE',
    },
    {
      agrcorliq_id: 4,
      agrcorliq_nombre: 'ASESORIAS ESPAÑA',
      agrcorliq_tipo: 'ASESORIAS_ES',
    },
  ];

  const contextualMenuItems = [
    {
      key: 'accept',
      text: t('Aceptado'),
      onClick: () => {
        handleAcepted();
      },
      iconProps: { iconName: 'ActivateOrders' },
      className: settlement && settlement.sexpasinc_estado === 'PREGENERADO' ? '' : 'hide-project-btn',
    },
    {
      key: 'eliminate',
      text: t('Eliminado'),
      onClick: () => {
        handleEliminated();
      },
      iconProps: { iconName: 'DeactivateOrders' },
      className:
        settlement && ['PREGENERADO', 'PENDIENTE'].includes(settlement.sexpasinc_estado)
          ? ''
          : 'hide-project-btn',
    },
  ];

  const menuProps: IContextualMenuProps = {
    shouldFocusOnMount: true,
    items: contextualMenuItems,
  };

  const getSettlementResponse = async () => {
    if (id) {
      props.changeBlockUI(true);
      try {
        const settlement = await props.system.getReportHoursSettlementSystem().detail(id);
        if (settlement) {
          setSettlement(settlement);
        }
      } catch (error) {
        console.log(error);
        props.courier.messageError(t('Error al cargar la liquidacion'));
      } finally {
        props.changeBlockUI(false);
      }
    }
  };

  const handleAcepted = async () => {
    if (id) {
      props.changeBlockUI(true);
      try {
        const response = await props.system.getReportHoursSettlementSystem().accept(id);
        if (response) {
          props.courier.messageSuccess(t('Liquidacion aceptada correctamente'));
        }
      } catch (error) {
        console.log(error);
        props.courier.messageError(t('Error al aceptar la liquidacion'));
      } finally {
        props.changeBlockUI(false);
        getSettlementResponse();
      }
    }
  };

  const handleEliminated = async () => {
    if (id) {
      props.changeBlockUI(true);
      try {
        const response = await props.system.getReportHoursSettlementSystem().eliminate(id);
        if (response) {
          props.courier.messageSuccess(t('Liquidacion eliminada correctamente'));
        }
      } catch (error) {
        console.log(error);
        props.courier.messageError(t('Error al eliminar la liquidacion'));
      } finally {
        props.changeBlockUI(false);
        getSettlementResponse();
      }
    }
  };

  const handleDownloadLiquidacion = async () => {
    if (!id) return;
    props.changeBlockUI(true);
    try {
      const { blob, filename } = await props.system.getReportHoursSettlementSystem().downloadInformeHoras(id);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
      props.courier.messageSuccess(t('Reporte descargado correctamente'));
    } catch (error) {
      console.log(error);
      props.courier.messageError(t('Error al descargar el reporte'));
    } finally {
      props.changeBlockUI(false);
    }
  };

  const handleTipoLiquidacion = (sexpasinc_agrcorliq_tipo) => {
    const agrupador = agrupadoresCorporacionLiquidadora.find((a) => a.agrcorliq_tipo === sexpasinc_agrcorliq_tipo);
    return agrupador ? agrupador.agrcorliq_nombre : 'NA';
  }

  useEffect(() => {
    if (id) {
      getSettlementResponse();
    }
  }, [id]);

  const itemsMenuCalendar = React.useMemo(
    (): ICommandBarItemProps[] => [
      {
        key: 'title',
        text: t('Liquidaciones reporte de horas'),
        iconProps: {
          iconName: 'ReminderPerson',
          color: DefaultPalette.themeLighterAlt,
          className: 'text-title-btn',
        },
        buttonStyles: titleCommandBarButtonStyles,
        className: 'text-title-btn',
      },
      {
        key: 'back',
        text: t('Volver'),
        iconProps: {
          iconName: 'Back',
          color: DefaultPalette.themeLighterAlt,
          className: 'text-title-btn',
        },
        buttonStyles: titleCommandBarButtonStyles,
        className: 'text-title-btn',
        onClick: () => {
          navigate(`${routes.report_hours_settlement}`);
        },
      },
    ],
    [],
  );

  return (
    <ViewWrapperWithCommandBar
      withBorderBottom
      title={''}
      items={itemsMenuCalendar}
      overflowButtonStyles={defaultCommandBarButtonStyles}
      variant="highligth"
      titleBody={t('Liquidaciones')}
    >
      {settlement && (
        <div className="details-container-rh">
          <div className="details-content-rh">
            {/* HEADER */}
            <div className="details-header-rh-container">
              <div className="details-header-rh">
                <Text variant="large" className="fw-500">
                  {t('Detalle de la solicitud: ') + id}
                </Text>
                <div className="details-header-right-rh">
                  <LiquidacionEstado status={settlement.sexpasinc_estado || ReportHoursSettlementEstado.REGISTRADO} />
                  {['PREGENERADO', 'PENDIENTE'].includes(
                    settlement.sexpasinc_estado
                  ) && (
                    <IconButton iconProps={GlobalNavButtonIcon} title="Opciones de actividad" ariaLabel="GlobalNavButton" menuProps={menuProps} />
                  )}
                </div>
              </div>

              {['PREGENERADO', 'ACEPTADO'].includes(settlement.sexpasinc_estado) && (
                <div className="details-buttons-rh">
                  <PrimaryButton
                    text={t('Descargar reporte')}
                    iconProps={{ iconName: 'ZipFolder', color: DefaultPalette.themeLighterAlt }}
                    onClick={handleDownloadLiquidacion}
                  />
                </div>
              )}
            </div>

            {settlement.sexpasinc_estado === 'PENDIENTE' && (
              <>
                <div className="details-info-periodo-tipo-rh">
                  <div className="detail-info-item-rh">
                    <span className="tts-liquidacion-label">{t('Periodo')}:</span>
                    <span className="tts-liquidacion-value tts-liquidacion-rh-value--padding">
                      {settlement.sexpasinc_fecha_ejecucion ? DateTime.fromFormat(settlement.sexpasinc_fecha_ejecucion, 'yyyy-MM-dd HH:mm:ss').toFormat('dd/MM/yyyy hh:mm:ss a') : 'N/A'}
                    </span>
                  </div>
                  <div className="detail-info-item-rh">
                    <span className="tts-liquidacion-label">{t('Tipo de liquidacion')}:</span>
                    <span className="tts-liquidacion-value tts-liquidacion-rh-value--padding">
                      {handleTipoLiquidacion(settlement.sexpasinc_agrcorliq_tipo) || 'N/A'}
                    </span>
                  </div>
                </div>

                <div className="details-info-2-rh">
                  <Icon iconName="Info" className="text-icon" />
                  <ul className="p-0">
                    <li>{t('En este estado se visualizan las equivalencias no encontradas.')}</li>
                    <li>{t('Colocar en la columna Detalle el proyecto y/o servicio correcto.')}</li>
                    <li>{t('Colocar en la columna Compania la informacion correcta.')}</li>
                    <li>{t('(*) Campo obligatorio')}</li>
                  </ul>
                </div>
              </>
            )}

            {['PREGENERADO', 'ACEPTADO', 'ELIMINADO'].includes(settlement.sexpasinc_estado) && (
              <>
                <div className="mb-2">
                  <div className="details-row-rh">
                    <Icon iconName="Calendar" className="details-icon-rh" />
                    <Text>
                      <b>{t('Periodo :')}</b>{' '}
                      <span className="details-highlight-rh">
                        {settlement.sexpasinc_fecha_ejecucion ? DateTime.fromFormat(settlement.sexpasinc_fecha_ejecucion, 'yyyy-MM-dd HH:mm:ss').toFormat('MM/yyyy') : 'N/A'}
                      </span>
                    </Text>
                  </div>

                  <div className="details-row-rh">
                    <Icon iconName="CalendarSettings" className="details-icon-rh" />
                    <Text>
                      <b>{t('Tipo de liquidacion:')}</b> {handleTipoLiquidacion(settlement.sexpasinc_agrcorliq_tipo) || 'N/A'}
                    </Text>
                  </div>

                  <div className="details-row-rh">
                    <Icon iconName="Trending12" className="details-icon-rh" />
                    <Text>
                      <b>{t('Estado:')}</b>{' '}
                      <span className="details-status-rh">{settlement.sexpasinc_estado.toLowerCase()}</span>
                    </Text>
                  </div>

                  <div className="details-row-rh">
                    <Icon iconName="EventDate" className="details-icon-rh" />
                    <Text>
                      <b>{t('Creada el:')}</b>{' '}
                      {settlement.sexpasinc_fecha_ejecucion ? DateTime.fromFormat(settlement.sexpasinc_fecha_ejecucion, 'yyyy-MM-dd HH:mm:ss').toFormat('dd/MM/yyyy hh:mm:ss a') : 'N/A'}
                    </Text>
                  </div>

                  <div className="details-row-rh">
                    <Icon iconName="Contact" className="details-icon-rh" />
                    <Text>
                      <b>{t('Generado por:')}</b> {settlement.colaborador || 'N/A'}
                    </Text>
                  </div>

                  {settlement.sexpasinc_estado === ReportHoursSettlementEstado.ACEPTADO && (
                    <div className="details-row-rh">
                      <Icon iconName="Contact" className="details-icon-rh" />
                      <Text>
                        <b>{t('Aceptado por:')}</b> {settlement.colaborador || 'N/A'}
                      </Text>
                    </div>
                  )}

                  {settlement.sexpasinc_estado === ReportHoursSettlementEstado.ELIMINADO && (
                    <div className="details-row-rh">
                      <Icon iconName="Contact" className="details-icon-rh" />
                      <Text>
                        <b>{t('Eliminado por:')}</b> {settlement.colaborador || 'N/A'}
                      </Text>
                    </div>
                  )}
                </div>

                <SettlementTablePreliquidadoReportHours
                  system={props.system}
                  courier={props.courier}
                  changeBlockUI={props.changeBlockUI}
                  id={id!}
                />
              </>
            )}
          </div>
        </div>
      )}
    </ViewWrapperWithCommandBar>
  );
};

export default ReportHoursSettlementDetail;
